import React, { useState, useEffect } from 'react';
import dashboardService from '../services/dashboardService';
import ScheduleMaintenance from './ScheduleMaintenance';
import BreakdownRequests from './BreakdownRequests';
import ExtensionReleaseRequests from './ExtensionReleaseRequests';
import AssistantSidebar from './AssistantSidebar';
import {
  PieChart, Pie, Cell, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip,
  ResponsiveContainer, Legend, AreaChart, Area
} from 'recharts';
import {
  FaBoxOpen, FaExclamationTriangle, FaChartLine,
  FaSpinner, FaSync, FaExclamationCircle, FaBell, FaCheckCircle,
  FaClock, FaMoneyBillWave, FaShieldAlt, FaClipboardList,
  FaWarehouse, FaWrench, FaCalendarAlt, FaTimesCircle,
  FaArrowUp, FaExchangeAlt, FaHeartbeat, FaUsers, FaUserCheck, FaUserClock,
  FaUserCog, FaPercentage, FaHourglassHalf
} from 'react-icons/fa';
import '../styles/AssistantHome.css';

const STATUS_COLORS = {
  available: '#10b981',
  in_use: '#3b82f6',
  under_maintenance: '#f59e0b',
  disposed: '#ef4444'
};
const CONDITION_COLORS = {
  excellent: '#10b981',
  good: '#3b82f6',
  fair: '#f59e0b',
  damaged: '#ef4444'
};

const REFRESH_INTERVAL = 30000; // 30 seconds

const AssistantHomeDashboard = ({ user }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [scheduleAsset, setScheduleAsset] = useState(null);
  const [showBreakdownRequests, setShowBreakdownRequests] = useState(false);
  const [showExtensionReleaseRequests, setShowExtensionReleaseRequests] = useState(false);
  const [lastUpdated, setLastUpdated] = useState(null);
  const [refreshing, setRefreshing] = useState(false);
  // const [viewAllAssets, setViewAllAssets] = useState(false);
  // const [allAssets, setAllAssets] = useState([]);
  // const [assetsLoading, setAssetsLoading] = useState(false);

  const fetchData = async (isBackground = false) => {
    try {
      if (!isBackground) setLoading(true);
      else setRefreshing(true);
      setError(null);
      const response = await dashboardService.getAssistantDashboard();
      if (response.success) {
        setData(response.data);
        setLastUpdated(new Date());
      }
    } catch (err) {
      if (!isBackground) setError('Failed to load dashboard data');
      console.error(err);
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  // const fetchAllAssets = async () => {
  //   try {
  //     setAssetsLoading(true);
  //     const token = sessionStorage.getItem('token');
  //     const response = await fetch(
  //       `${process.env.REACT_APP_API_BASE_URL || 'http://localhost:8000'}/api/assets?viewAll=true`,
  //       {
  //         headers: { Authorization: `Bearer ${token}` }
  //       }
  //     );
  //     const result = await response.json();
  //     if (result.success) {
  //       setAllAssets(result.data || []);
  //     } else {
  //       setAllAssets([]);
  //     }
  //   } catch (err) {
  //     console.error('Error fetching assets:', err);
  //     setAllAssets([]);
  //   } finally {
  //     setAssetsLoading(false);
  //   }
  // };

  // const handleViewAllAssets = () => {
  //   if (!viewAllAssets) {
  //     fetchAllAssets();
  //   }
  //   setViewAllAssets(!viewAllAssets);
  // };

  useEffect(() => {
    fetchData();
    const interval = setInterval(() => fetchData(true), REFRESH_INTERVAL);
    return () => clearInterval(interval);
  }, []);

  // Auto-refresh assets when tab is open (commented out with View All Assets)
  // useEffect(() => {
  //   if (viewAllAssets) {
  //     fetchAllAssets();
  //     const assetsInterval = setInterval(() => fetchAllAssets(), REFRESH_INTERVAL);
  //     return () => clearInterval(assetsInterval);
  //   }
  // }, [viewAllAssets]);

  if (loading) {
    return (
      <div className="ah-loading">
        <FaSpinner className="spin" />
        <p>Loading your dashboard...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="ah-error">
        <FaExclamationCircle />
        <p>{error}</p>
        <button onClick={fetchData}>Retry</button>
      </div>
    );
  }

  if (!data) return null;

  const { assets = {}, alerts = {}, issues = {}, maintenance = {}, assetRequests = {}, assignments = {}, users = {} } = data;
  
  // Set default values if properties are missing
  const defaultAssets = {
    available: 0,
    inUse: 0,
    underMaintenance: 0,
    disposed: 0,
    total: 0,
    totalValue: 0,
    assigned: 0,
    unassigned: 0,
    utilizationRate: 0,
    assignmentRate: 0,
    categories: [],
    condition: { excellent: 0, good: 0, fair: 0, damaged: 0 },
    ...assets
  };

  const defaultAlerts = {
    lowStock: [],
    warrantyExpiring: [],
    warrantyExpired: [],
    maintenanceDueSoon: [],
    overdueMaintenanceAssets: [],
    ...alerts
  };

  const defaultIssues = {
    total: 0,
    pending: 0,
    active: 0,
    resolved: 0,
    rejected: 0,
    byType: { breakdown: 0, preventive: 0, calibration: 0 },
    bySeverity: { low: 0, medium: 0, high: 0 },
    resolutionRate: 0,
    avgResolutionDays: 0,
    byMonth: [],
    recent: [],
    ...issues
  };

  const defaultMaintenance = {
    totalLogs: 0,
    pending: 0,
    completed: 0,
    totalCost: 0,
    ...maintenance
  };

  const defaultRequests = {
    total: 0,
    pending: 0,
    approved: 0,
    rejected: 0,
    ...assetRequests
  };

  const defaultAssignments = {
    active: 0,
    ...assignments
  };

  const defaultUsers = {
    total: 0,
    active: 0,
    inactive: 0,
    approved: 0,
    pendingApproval: 0,
    byRole: { asset_incharge: 0, asset_technician: 0, admin: 0, asset_user: 0 },
    ...users
  };

  const userData = data.user;

  // Compute health score (weighted: excellent=100, good=75, fair=50, damaged=25)
  const totalConditionAssets = defaultAssets.condition.excellent + defaultAssets.condition.good + defaultAssets.condition.fair + defaultAssets.condition.damaged;
  const healthScore = totalConditionAssets > 0
    ? Math.round(((defaultAssets.condition.excellent * 100) + (defaultAssets.condition.good * 75) + (defaultAssets.condition.fair * 50) + (defaultAssets.condition.damaged * 25)) / totalConditionAssets)
    : 0;

  const assetStatusData = [
    { name: 'Available', value: defaultAssets.available, color: STATUS_COLORS.available },
    { name: 'In Use', value: defaultAssets.inUse, color: STATUS_COLORS.in_use },
    { name: 'Maintenance', value: defaultAssets.underMaintenance, color: STATUS_COLORS.under_maintenance },
    { name: 'Disposed', value: defaultAssets.disposed, color: STATUS_COLORS.disposed }
  ].filter(d => d.value > 0);

  const conditionData = [
    { name: 'Excellent', value: defaultAssets.condition.excellent, color: CONDITION_COLORS.excellent },
    { name: 'Good', value: defaultAssets.condition.good, color: CONDITION_COLORS.good },
    { name: 'Fair', value: defaultAssets.condition.fair, color: CONDITION_COLORS.fair },
    { name: 'Damaged', value: defaultAssets.condition.damaged, color: CONDITION_COLORS.damaged }
  ].filter(d => d.value > 0);

  const issueTypeData = [
    { name: 'Breakdown', value: defaultIssues.byType.breakdown, color: '#ef4444' },
    { name: 'Preventive', value: defaultIssues.byType.preventive, color: '#3b82f6' },
    { name: 'Calibration', value: defaultIssues.byType.calibration, color: '#8b5cf6' }
  ].filter(d => d.value > 0);

  const severityData = [
    { name: 'High', value: defaultIssues.bySeverity.high, color: '#ef4444' },
    { name: 'Medium', value: defaultIssues.bySeverity.medium, color: '#f59e0b' },
    { name: 'Low', value: defaultIssues.bySeverity.low, color: '#10b981' }
  ].filter(d => d.value > 0);

  const userRoleData = [
    { name: 'Asset Users', value: defaultUsers.byRole.asset_user, color: '#3b82f6' },
    { name: 'Technicians', value: defaultUsers.byRole.asset_technician, color: '#8b5cf6' },
    { name: 'Incharge', value: defaultUsers.byRole.asset_incharge, color: '#10b981' },
    { name: 'Lab Admin', value: defaultUsers.byRole.admin, color: '#f59e0b' }
  ].filter(d => d.value > 0);

  const totalAlerts = defaultAlerts.lowStock.length + defaultAlerts.warrantyExpiring.length +
    (defaultAlerts.warrantyExpired?.length || 0) +
    defaultAlerts.maintenanceDueSoon.length + defaultAlerts.overdueMaintenanceAssets.length;

  // Assignment data for the horizontal bar
  const assignmentBarData = [
    { name: 'Assets', assigned: defaultAssets.assigned, unassigned: defaultAssets.unassigned }
  ];

  return (
    <div className="assistant-page">
      <AssistantSidebar />
      <div className="page-container">
        <div className="ah-dashboard">
          {/* Welcome Header */}
          <div className="ah-header">
            <div className="ah-welcome">
             
              <div>
                <h1>Welcome, {userData?.name || user?.name || 'Asset Incharge'}</h1>
                <p>
                  <FaWarehouse style={{ marginRight: 6 }} />
                  {user?.department || userData?.department || 'No department assigned'} — Dashboard
                </p>
              </div>
            </div>
            <div className="ah-header-actions">
              {lastUpdated && (
                <span className="ah-last-updated">
                  <FaClock style={{ marginRight: 4 }} />
                  Updated {lastUpdated.toLocaleTimeString()}
                </span>
              )}
              <button className="btn-refresh-ah" onClick={() => fetchData(false)} disabled={refreshing}>
                <FaSync className={refreshing ? 'spin' : ''} /> {refreshing ? 'Refreshing...' : 'Refresh'}
              </button>
            </div>
          </div>

          {/* Alerts Banner */}
          {totalAlerts > 0 && (
            <div className="ah-alerts-banner">
              <FaBell className="alert-bell" />
              <span className="alert-text">
                <strong>{totalAlerts} Alert{totalAlerts > 1 ? 's' : ''}</strong> —
                {defaultAlerts.lowStock.length > 0 && ` ${defaultAlerts.lowStock.length} low stock`}
                {defaultAlerts.warrantyExpiring.length > 0 && ` • ${defaultAlerts.warrantyExpiring.length} warranty expiring`}
                {(defaultAlerts.warrantyExpired?.length || 0) > 0 && ` • ${defaultAlerts.warrantyExpired.length} warranty expired`}
                {defaultAlerts.maintenanceDueSoon.length > 0 && ` • ${defaultAlerts.maintenanceDueSoon.length} maintenance due soon`}
                {defaultAlerts.overdueMaintenanceAssets.length > 0 && ` • ${defaultAlerts.overdueMaintenanceAssets.length} overdue maintenance`}
              </span>
            </div>
          )}

          {/* ===== Hero Stat Cards ===== */}
          <div className="ah-hero-grid">
            <div className="ah-hero-card">
              <div className="ah-hero-top">
                <span className="ah-hero-label">Total Assets</span>
                <span className="ah-hero-icon-wrap blue"><FaBoxOpen /></span>
              </div>
              <span className="ah-hero-value">{defaultAssets.total}</span>
              <div className="ah-hero-breakdown">
                <span className="ah-mini-stat green"><FaArrowUp /> {defaultAssets.available} available</span>
                <span className="ah-mini-stat blue"><FaExchangeAlt /> {defaultAssets.inUse} in use</span>
              </div>
            </div>

            <div className="ah-hero-card">
              <div className="ah-hero-top">
                <span className="ah-hero-label">Asset Health</span>
                <span className="ah-hero-icon-wrap green"><FaHeartbeat /></span>
              </div>
              <span className="ah-hero-value">{healthScore}<small>%</small></span>
              <div className="ah-hero-bar">
                <div className="ah-hero-bar-fill" style={{ width: `${healthScore}%`, background: healthScore >= 70 ? '#10b981' : healthScore >= 50 ? '#f59e0b' : '#ef4444' }}></div>
              </div>
              <span className="ah-hero-hint">{healthScore >= 70 ? 'Healthy fleet' : healthScore >= 50 ? 'Needs attention' : 'Critical'}</span>
            </div>

            <div className="ah-hero-card">
              <div className="ah-hero-top">
                <span className="ah-hero-label">Pending Actions</span>
                <span className="ah-hero-icon-wrap orange"><FaClock /></span>
              </div>
              <span className="ah-hero-value">{defaultRequests.pending + defaultIssues.pending + defaultMaintenance.pending}</span>
              <div className="ah-hero-breakdown">
                <span className="ah-mini-stat orange">{defaultRequests.pending} requests</span>
                <span className="ah-mini-stat red">{defaultIssues.pending} issues</span>
                <span className="ah-mini-stat amber">{defaultMaintenance.pending} maint.</span>
              </div>
            </div>

            <div className="ah-hero-card">
              <div className="ah-hero-top">
                <span className="ah-hero-label">Portfolio Value</span>
                <span className="ah-hero-icon-wrap purple"><FaMoneyBillWave /></span>
              </div>
              <span className="ah-hero-value">₹{defaultAssets.totalValue.toLocaleString()}</span>
              <div className="ah-hero-breakdown">
                <span className="ah-mini-stat teal">{defaultAssets.utilizationRate}% utilized</span>
                <span className="ah-mini-stat blue">{defaultAssets.assignmentRate}% assigned</span>
              </div>
            </div>
          </div>

          {/* ===== Quick Insights Row ===== */}
          <div className="ah-insights-row">
            <div className="ah-insight-card">
              <div className="ah-insight-icon blue"><FaUsers /></div>
              <div className="ah-insight-info">
                <span className="ah-insight-value">{defaultUsers.total}</span>
                <span className="ah-insight-label">Dept. Users</span>
              </div>
            </div>
            <div className="ah-insight-card">
              <div className="ah-insight-icon green"><FaPercentage /></div>
              <div className="ah-insight-info">
                <span className="ah-insight-value">{defaultIssues.resolutionRate}%</span>
                <span className="ah-insight-label">Issue Resolution</span>
              </div>
            </div>
            <div className="ah-insight-card">
              <div className="ah-insight-icon orange"><FaHourglassHalf /></div>
              <div className="ah-insight-info">
                <span className="ah-insight-value">{defaultIssues.avgResolutionDays}d</span>
                <span className="ah-insight-label">Avg. Resolution</span>
              </div>
            </div>
            <div className="ah-insight-card">
              <div className="ah-insight-icon purple"><FaExchangeAlt /></div>
              <div className="ah-insight-info">
                <span className="ah-insight-value">{defaultAssignments.active}</span>
                <span className="ah-insight-label">Active Assignments</span>
              </div>
            </div>
            <div className="ah-insight-card">
              <div className="ah-insight-icon red"><FaExclamationTriangle /></div>
              <div className="ah-insight-info">
                <span className="ah-insight-value">{defaultIssues.bySeverity.high}</span>
                <span className="ah-insight-label">High Severity</span>
              </div>
            </div>
            <div className="ah-insight-card">
              <div className="ah-insight-icon teal"><FaUserCheck /></div>
              <div className="ah-insight-info">
                <span className="ah-insight-value">{defaultUsers.active}</span>
                <span className="ah-insight-label">Active Users</span>
              </div>
            </div>
          </div>

          {/* ===== Charts Section ===== */}
          <div className="ah-charts-grid">
            {/* Asset Status Donut */}
            <div className="ah-chart-card">
              <div className="ah-chart-header">
                <h3><FaBoxOpen /> Asset Status</h3>
                <span className="ah-chart-count">{defaultAssets.total} total</span>
              </div>
              {assetStatusData.length > 0 ? (
                <ResponsiveContainer width="100%" height={240}>
                  <PieChart>
                    <Pie data={assetStatusData} cx="50%" cy="50%" outerRadius={80} innerRadius={50} dataKey="value" paddingAngle={3} label={({ name, value }) => `${name}: ${value}`}>
                      {assetStatusData.map((entry, idx) => (
                        <Cell key={idx} fill={entry.color} />
                      ))}
                    </Pie>
                    <Tooltip />
                    <Legend iconType="circle" iconSize={8} />
                  </PieChart>
                </ResponsiveContainer>
              ) : <p className="ah-no-data">No asset data</p>}
            </div>

            {/* Asset Condition Donut */}
            <div className="ah-chart-card">
              <div className="ah-chart-header">
                <h3><FaHeartbeat /> Condition Breakdown</h3>
              </div>
              {conditionData.length > 0 ? (
                <ResponsiveContainer width="100%" height={240}>
                  <PieChart>
                    <Pie data={conditionData} cx="50%" cy="50%" outerRadius={80} innerRadius={50} dataKey="value" paddingAngle={3} label={({ name, value }) => `${name}: ${value}`}>
                      {conditionData.map((entry, idx) => (
                        <Cell key={idx} fill={entry.color} />
                      ))}
                    </Pie>
                    <Tooltip />
                    <Legend iconType="circle" iconSize={8} />
                  </PieChart>
                </ResponsiveContainer>
              ) : <p className="ah-no-data">No condition data</p>}
            </div>

            {/* Categories Bar */}
            <div className="ah-chart-card">
              <div className="ah-chart-header">
                <h3><FaChartLine /> Categories</h3>
                <span className="ah-chart-count">{defaultAssets.categories.length} types</span>
              </div>
              {defaultAssets.categories.length > 0 ? (
                <ResponsiveContainer width="100%" height={240}>
                  <BarChart data={defaultAssets.categories} layout="vertical" margin={{ left: 10 }}>
                    <CartesianGrid strokeDasharray="3 3" horizontal={false} />
                    <XAxis type="number" allowDecimals={false} />
                    <YAxis dataKey="category" type="category" tick={{ fontSize: 12 }} width={100} />
                    <Tooltip />
                    <Bar dataKey="count" fill="#3b82f6" radius={[0, 6, 6, 0]} barSize={20} />
                  </BarChart>
                </ResponsiveContainer>
              ) : <p className="ah-no-data">No categories</p>}
            </div>
          </div>

          {/* ===== Activity Row: Issues + Assignments + Maintenance ===== */}
          <div className="ah-activity-row">
            {/* Issues Card */}
            <div className="ah-activity-card">
              <div className="ah-activity-header">
                <FaExclamationTriangle className="ah-activity-icon" />
                <h3>Issues</h3>
                <span className="ah-activity-total">{defaultIssues.total}</span>
              </div>
              <div className="ah-stat-bars">
                <div className="ah-stat-bar-item">
                  <span className="ah-bar-label">Pending</span>
                  <div className="ah-bar-track">
                    <div className="ah-bar-fill orange" style={{ width: defaultIssues.total > 0 ? `${(defaultIssues.pending / defaultIssues.total) * 100}%` : '0%' }}></div>
                  </div>
                  <span className="ah-bar-value">{defaultIssues.pending}</span>
                </div>
                <div className="ah-stat-bar-item">
                  <span className="ah-bar-label">Active</span>
                  <div className="ah-bar-track">
                    <div className="ah-bar-fill blue" style={{ width: defaultIssues.total > 0 ? `${(defaultIssues.active / defaultIssues.total) * 100}%` : '0%' }}></div>
                  </div>
                  <span className="ah-bar-value">{defaultIssues.active}</span>
                </div>
                <div className="ah-stat-bar-item">
                  <span className="ah-bar-label">Resolved</span>
                  <div className="ah-bar-track">
                    <div className="ah-bar-fill green" style={{ width: defaultIssues.total > 0 ? `${(defaultIssues.resolved / defaultIssues.total) * 100}%` : '0%' }}></div>
                  </div>
                  <span className="ah-bar-value">{defaultIssues.resolved}</span>
                </div>
                <div className="ah-stat-bar-item">
                  <span className="ah-bar-label">Rejected</span>
                  <div className="ah-bar-track">
                    <div className="ah-bar-fill red" style={{ width: defaultIssues.total > 0 ? `${(defaultIssues.rejected / defaultIssues.total) * 100}%` : '0%' }}></div>
                  </div>
                  <span className="ah-bar-value">{defaultIssues.rejected}</span>
                </div>
              </div>
              {issueTypeData.length > 0 && (
                <div className="ah-mini-chart">
                  <ResponsiveContainer width="100%" height={120}>
                    <PieChart>
                      <Pie data={issueTypeData} cx="50%" cy="50%" outerRadius={45} innerRadius={28} dataKey="value" paddingAngle={3}>
                        {issueTypeData.map((entry, idx) => (
                          <Cell key={idx} fill={entry.color} />
                        ))}
                      </Pie>
                      <Tooltip />
                    </PieChart>
                  </ResponsiveContainer>
                  <div className="ah-mini-legend">
                    {issueTypeData.map(d => (
                      <span key={d.name} className="ah-legend-item">
                        <span className="ah-legend-dot" style={{ background: d.color }}></span> {d.name}: {d.value}
                      </span>
                    ))}
                  </div>
                </div>
              )}
            </div>

            {/* Requests & Assignments Card */}
            <div className="ah-activity-card">
              <div className="ah-activity-header">
                <FaClipboardList className="ah-activity-icon" />
                <h3>Requests & Assignments</h3>
              </div>
              <div className="ah-ra-grid">
                <div className="ah-ra-item pending-bg">
                  <span className="ah-ra-number">{defaultRequests.pending}</span>
                  <span className="ah-ra-label">Pending</span>
                </div>
                <div className="ah-ra-item approved-bg">
                  <span className="ah-ra-number">{defaultRequests.approved}</span>
                  <span className="ah-ra-label">Approved</span>
                </div>
                <div className="ah-ra-item rejected-bg">
                  <span className="ah-ra-number">{defaultRequests.rejected}</span>
                  <span className="ah-ra-label">Rejected</span>
                </div>
                <div className="ah-ra-item active-bg">
                  <span className="ah-ra-number">{defaultAssignments.active}</span>
                  <span className="ah-ra-label">Active Assign.</span>
                </div>
              </div>
              <div className="ah-assignment-viz">
                <span className="ah-assign-label">Assignment Ratio</span>
                <ResponsiveContainer width="100%" height={50}>
                  <BarChart data={assignmentBarData} layout="vertical" margin={{ top: 0, right: 0, bottom: 0, left: 0 }}>
                    <XAxis type="number" hide />
                    <YAxis dataKey="name" type="category" hide />
                    <Tooltip />
                    <Bar dataKey="assigned" stackId="a" fill="#10b981" radius={[6, 0, 0, 6]} name="Assigned" />
                    <Bar dataKey="unassigned" stackId="a" fill="#e2e8f0" radius={[0, 6, 6, 0]} name="Unassigned" />
                  </BarChart>
                </ResponsiveContainer>
                <div className="ah-assign-legend">
                  <span><span className="ah-legend-dot" style={{ background: '#10b981' }}></span> Assigned: {defaultAssets.assigned}</span>
                  <span><span className="ah-legend-dot" style={{ background: '#e2e8f0', border: '1px solid #cbd5e1' }}></span> Unassigned: {defaultAssets.unassigned}</span>
                </div>
              </div>
            </div>

            {/* Maintenance Card */}
            <div className="ah-activity-card">
              <div className="ah-activity-header">
                <FaWrench className="ah-activity-icon" />
                <h3>Maintenance</h3>
                <span className="ah-activity-total">{defaultMaintenance.totalLogs}</span>
              </div>
              <div className="ah-stat-bars">
                <div className="ah-stat-bar-item">
                  <span className="ah-bar-label">Pending</span>
                  <div className="ah-bar-track">
                    <div className="ah-bar-fill orange" style={{ width: defaultMaintenance.totalLogs > 0 ? `${(defaultMaintenance.pending / defaultMaintenance.totalLogs) * 100}%` : '0%' }}></div>
                  </div>
                  <span className="ah-bar-value">{defaultMaintenance.pending}</span>
                </div>
                <div className="ah-stat-bar-item">
                  <span className="ah-bar-label">Completed</span>
                  <div className="ah-bar-track">
                    <div className="ah-bar-fill green" style={{ width: defaultMaintenance.totalLogs > 0 ? `${(defaultMaintenance.completed / defaultMaintenance.totalLogs) * 100}%` : '0%' }}></div>
                  </div>
                  <span className="ah-bar-value">{defaultMaintenance.completed}</span>
                </div>
              </div>
              <div className="ah-maint-cost">
                <FaMoneyBillWave />
                <div>
                  <span className="ah-maint-cost-label">Total Maintenance Cost</span>
                  <span className="ah-maint-cost-value">₹{defaultMaintenance.totalCost.toLocaleString()}</span>
                </div>
              </div>
            </div>
          </div>

          {/* ===== Users & Issue Insights Row ===== */}
          <div className="ah-insights-detail-row">
            {/* Department Users Card */}
            <div className="ah-detail-card">
              <div className="ah-detail-header">
                <FaUsers className="ah-detail-icon" />
                <h3>Department Users</h3>
                <span className="ah-detail-total">{defaultUsers.total}</span>
              </div>
              <div className="ah-user-stats-grid">
                <div className="ah-user-stat-item">
                  <FaUserCog className="role-icon incharge" />
                  <span className="ah-user-stat-number">{defaultUsers.byRole.asset_incharge}</span>
                  <span className="ah-user-stat-label">Incharge</span>
                </div>
                <div className="ah-user-stat-item">
                  <FaWrench className="role-icon technician" />
                  <span className="ah-user-stat-number">{defaultUsers.byRole.asset_technician}</span>
                  <span className="ah-user-stat-label">Technicians</span>
                </div>
                <div className="ah-user-stat-item">
                  <FaUsers className="role-icon user" />
                  <span className="ah-user-stat-number">{defaultUsers.byRole.asset_user}</span>
                  <span className="ah-user-stat-label">Asset Users</span>
                </div>
                <div className="ah-user-stat-item">
                  <FaShieldAlt className="role-icon admin" />
                  <span className="ah-user-stat-number">{defaultUsers.byRole.admin}</span>
                  <span className="ah-user-stat-label">Lab Admins</span>
                </div>
              </div>
              <div className="ah-user-status-row">
                <div className="ah-user-status-item active">
                  <FaUserCheck /> <span>{defaultUsers.active} Active</span>
                </div>
                <div className="ah-user-status-item pending">
                  <FaUserClock /> <span>{defaultUsers.pendingApproval} Pending</span>
                </div>
                <div className="ah-user-status-item inactive">
                  <FaTimesCircle /> <span>{defaultUsers.inactive} Inactive</span>
                </div>
              </div>
              {userRoleData.length > 0 && (
                <div className="ah-user-chart">
                  <ResponsiveContainer width="100%" height={130}>
                    <PieChart>
                      <Pie data={userRoleData} cx="50%" cy="50%" outerRadius={50} innerRadius={30} dataKey="value" paddingAngle={3}>
                        {userRoleData.map((entry, idx) => (
                          <Cell key={idx} fill={entry.color} />
                        ))}
                      </Pie>
                      <Tooltip />
                    </PieChart>
                  </ResponsiveContainer>
                </div>
              )}
            </div>

            {/* Issue Severity Card */}
            <div className="ah-detail-card">
              <div className="ah-detail-header">
                <FaExclamationTriangle className="ah-detail-icon" />
                <h3>Issue Severity</h3>
                <span className="ah-detail-total">{defaultIssues.total} total</span>
              </div>
              <div className="ah-severity-bars">
                <div className="ah-severity-item">
                  <div className="ah-severity-label-row">
                    <span className="ah-severity-dot high"></span>
                    <span>High</span>
                    <span className="ah-severity-count">{defaultIssues.bySeverity.high}</span>
                  </div>
                  <div className="ah-severity-track">
                    <div className="ah-severity-fill high" style={{ width: defaultIssues.total > 0 ? `${(defaultIssues.bySeverity.high / defaultIssues.total) * 100}%` : '0%' }}></div>
                  </div>
                </div>
                <div className="ah-severity-item">
                  <div className="ah-severity-label-row">
                    <span className="ah-severity-dot medium"></span>
                    <span>Medium</span>
                    <span className="ah-severity-count">{defaultIssues.bySeverity.medium}</span>
                  </div>
                  <div className="ah-severity-track">
                    <div className="ah-severity-fill medium" style={{ width: defaultIssues.total > 0 ? `${(defaultIssues.bySeverity.medium / defaultIssues.total) * 100}%` : '0%' }}></div>
                  </div>
                </div>
                <div className="ah-severity-item">
                  <div className="ah-severity-label-row">
                    <span className="ah-severity-dot low"></span>
                    <span>Low</span>
                    <span className="ah-severity-count">{defaultIssues.bySeverity.low}</span>
                  </div>
                  <div className="ah-severity-track">
                    <div className="ah-severity-fill low" style={{ width: defaultIssues.total > 0 ? `${(defaultIssues.bySeverity.low / defaultIssues.total) * 100}%` : '0%' }}></div>
                  </div>
                </div>
              </div>
              {severityData.length > 0 && (
                <div className="ah-severity-chart">
                  <ResponsiveContainer width="100%" height={130}>
                    <PieChart>
                      <Pie data={severityData} cx="50%" cy="50%" outerRadius={50} innerRadius={30} dataKey="value" paddingAngle={3}>
                        {severityData.map((entry, idx) => (
                          <Cell key={idx} fill={entry.color} />
                        ))}
                      </Pie>
                      <Tooltip />
                    </PieChart>
                  </ResponsiveContainer>
                </div>
              )}
            </div>

            {/* Issue Resolution Card */}
            <div className="ah-detail-card">
              <div className="ah-detail-header">
                <FaCheckCircle className="ah-detail-icon" />
                <h3>Resolution Metrics</h3>
              </div>
              <div className="ah-resolution-stats">
                <div className="ah-resolution-main">
                  <div className="ah-resolution-circle" style={{ background: `conic-gradient(#10b981 ${defaultIssues.resolutionRate * 3.6}deg, #e2e8f0 0deg)` }}>
                    <span className="ah-resolution-pct">{defaultIssues.resolutionRate}%</span>
                  </div>
                  <span className="ah-resolution-label">Resolution Rate</span>
                </div>
                <div className="ah-resolution-details">
                  <div className="ah-res-detail-item">
                    <FaHourglassHalf className="ah-res-icon orange" />
                    <div>
                      <span className="ah-res-value">{defaultIssues.avgResolutionDays} days</span>
                      <span className="ah-res-sublabel">Avg. Resolution Time</span>
                    </div>
                  </div>
                  <div className="ah-res-detail-item">
                    <FaCheckCircle className="ah-res-icon green" />
                    <div>
                      <span className="ah-res-value">{defaultIssues.resolved}</span>
                      <span className="ah-res-sublabel">Issues Resolved</span>
                    </div>
                  </div>
                  <div className="ah-res-detail-item">
                    <FaClock className="ah-res-icon blue" />
                    <div>
                      <span className="ah-res-value">{defaultIssues.active}</span>
                      <span className="ah-res-sublabel">In Progress</span>
                    </div>
                  </div>
                  <div className="ah-res-detail-item">
                    <FaTimesCircle className="ah-res-icon red" />
                    <div>
                      <span className="ah-res-value">{defaultIssues.rejected}</span>
                      <span className="ah-res-sublabel">Rejected</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* ===== Issue Trend Chart ===== */}
          {defaultIssues.byMonth.length > 0 && (
            <div className="ah-trend-card">
              <div className="ah-chart-header">
                <h3><FaCalendarAlt /> Issue Trend (Last 6 Months)</h3>
              </div>
              <ResponsiveContainer width="100%" height={250}>
                <AreaChart data={defaultIssues.byMonth}>
                  <defs>
                    <linearGradient id="gradBreakdown" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="5%" stopColor="#ef4444" stopOpacity={0.2} />
                      <stop offset="95%" stopColor="#ef4444" stopOpacity={0} />
                    </linearGradient>
                    <linearGradient id="gradPreventive" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="5%" stopColor="#3b82f6" stopOpacity={0.2} />
                      <stop offset="95%" stopColor="#3b82f6" stopOpacity={0} />
                    </linearGradient>
                    <linearGradient id="gradCalibration" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="5%" stopColor="#8b5cf6" stopOpacity={0.2} />
                      <stop offset="95%" stopColor="#8b5cf6" stopOpacity={0} />
                    </linearGradient>
                  </defs>
                  <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" />
                  <XAxis dataKey="month" tick={{ fontSize: 12 }} />
                  <YAxis allowDecimals={false} tick={{ fontSize: 12 }} />
                  <Tooltip />
                  <Legend iconType="circle" iconSize={8} />
                  <Area type="monotone" dataKey="breakdown" name="Breakdown" stroke="#ef4444" fill="url(#gradBreakdown)" strokeWidth={2} />
                  <Area type="monotone" dataKey="preventive" name="Preventive" stroke="#3b82f6" fill="url(#gradPreventive)" strokeWidth={2} />
                  <Area type="monotone" dataKey="calibration" name="Calibration" stroke="#8b5cf6" fill="url(#gradCalibration)" strokeWidth={2} />
                </AreaChart>
              </ResponsiveContainer>
            </div>
          )}

          {/* ===== Toggle: Dashboard / All Assets (commented out) =====
          <div className="ah-view-toggle">
            <button
              className={`ah-toggle-btn ${!viewAllAssets ? 'active' : ''}`}
              onClick={() => setViewAllAssets(false)}
            >
              <FaClipboardList /> Overview
            </button>
            <button
              className={`ah-toggle-btn ${viewAllAssets ? 'active' : ''}`}
              onClick={handleViewAllAssets}
            >
              <FaBoxOpen /> All Assets
            </button>
          </div>

          {viewAllAssets ? (
            <div className="ah-assets-view">
              <div className="ah-assets-header">
                <h2><FaBoxOpen style={{ marginRight: 8 }} /> All Assets (All Departments)</h2>
                <button className="btn-refresh-ah" onClick={fetchAllAssets} disabled={assetsLoading}>
                  <FaSync className={assetsLoading ? 'spin' : ''} /> {assetsLoading ? 'Refreshing...' : 'Refresh'}
                </button>
              </div>

              {assetsLoading && allAssets.length === 0 ? (
                <div className="ah-loading-inline">
                  <FaSpinner className="spin" /> Loading assets...
                </div>
              ) : allAssets.length === 0 ? (
                <p className="ah-no-data">No assets found.</p>
              ) : (
                <div className="ah-assets-table-wrapper">
                  <table className="ah-assets-table">
                    <thead>
                      <tr>
                        <th>Asset Name</th>
                        <th>Serial #</th>
                        <th>Category</th>
                        <th>Department</th>
                        <th>Brand / Model</th>
                        <th>Status</th>
                        <th>Condition</th>
                        <th>Assigned</th>
                        <th>Value (₹)</th>
                      </tr>
                    </thead>
                    <tbody>
                      {allAssets.map((asset) => (
                        <tr key={asset._id}>
                          <td className="asset-name-cell"><strong>{asset.assetName}</strong></td>
                          <td>{asset.serialNumber || '—'}</td>
                          <td><span className="ah-category-badge">{asset.category}</span></td>
                          <td>{asset.department || '—'}</td>
                          <td>{[asset.brand, asset.model].filter(Boolean).join(' / ') || '—'}</td>
                          <td>
                            <span className={`ah-status-badge status-${asset.status}`}>
                              {(asset.status || '').replace('_', ' ')}
                            </span>
                          </td>
                          <td>
                            <span className={`ah-condition-badge condition-${asset.condition}`}>
                              {asset.condition}
                            </span>
                          </td>
                          <td>
                            <span className={`ah-assigned-badge ${asset.isAssigned ? 'assigned' : 'unassigned'}`}>
                              {asset.isAssigned ? 'Yes' : 'No'}
                            </span>
                          </td>
                          <td className="ah-value-cell">{asset.purchaseCost ? `₹${asset.purchaseCost.toLocaleString()}` : '—'}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          ) : (
            <>
          ===== */}

              {/* ===== Bottom: Alerts + Recent Issues ===== */}
              <div className="ah-bottom-row-v2">
                {/* Alerts Panel */}
                <div className="ah-alerts-card">
                  <h3><FaBell style={{ marginRight: 8 }} /> Alerts & Notifications</h3>
                  {totalAlerts === 0 ? (
                    <div className="ah-no-alerts">
                      <FaCheckCircle />
                      <p>All clear! No pending alerts.</p>
                    </div>
                  ) : (
                    <div className="ah-alert-list">
                      {defaultAlerts.lowStock.map(a => (
                        <div key={a._id} className="ah-alert-item low-stock">
                          <FaBoxOpen />
                          <span><strong>{a.name}</strong> — Stock: {a.quantity}/{a.minQuantity}</span>
                        </div>
                      ))}
                      {(defaultAlerts.warrantyExpired || []).map(a => (
                        <div key={a._id} className="ah-alert-item warranty-expired">
                          <FaTimesCircle />
                          <span><strong>{a.name}</strong> — Warranty expired {new Date(a.expiryDate).toLocaleDateString()}</span>
                        </div>
                      ))}
                      {defaultAlerts.warrantyExpiring.map(a => (
                        <div key={a._id} className="ah-alert-item warranty">
                          <FaShieldAlt />
                          <span><strong>{a.name}</strong> — Warranty expires {new Date(a.expiryDate).toLocaleDateString()}</span>
                        </div>
                      ))}
                      {defaultAlerts.overdueMaintenanceAssets.map(a => (
                        <div key={a._id} className="ah-alert-item overdue">
                          <FaExclamationTriangle />
                          <div className="ah-alert-content">
                            <span><strong>{a.name}</strong> — Maintenance overdue since {new Date(a.nextDue).toLocaleDateString()}</span>
                            <button className="ah-alert-action" onClick={() => setScheduleAsset(a)}>
                              <FaCalendarAlt /> Schedule Now
                            </button>
                          </div>
                        </div>
                      ))}
                      {defaultAlerts.maintenanceDueSoon.map(a => (
                        <div key={a._id} className="ah-alert-item maint-due">
                          <FaClock />
                          <div className="ah-alert-content">
                            <span><strong>{a.name}</strong> — Maintenance due {new Date(a.nextDue).toLocaleDateString()}</span>
                            <button className="ah-alert-action" onClick={() => setScheduleAsset(a)}>
                              <FaCalendarAlt /> Schedule
                            </button>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>

                {/* Recent Issues */}
                <div className="ah-recent-card">
                  <h3><FaClipboardList style={{ marginRight: 8 }} /> Recent Issues</h3>
                  {defaultIssues.recent.length > 0 ? (
                    <div className="ah-recent-list">
                      {defaultIssues.recent.map(issue => (
                        <div key={issue._id} className="ah-recent-item">
                          <div className="ah-recent-meta">
                            <span className={`ah-badge type-${issue.issueType}`}>{issue.issueType}</span>
                            <span className={`ah-badge severity-${issue.severity}`}>{issue.severity}</span>
                            <span className={`ah-badge status-${issue.status}`}>{issue.status.replace('_', ' ')}</span>
                          </div>
                          <div className="ah-recent-body">
                            <strong>{issue.asset}</strong>
                            <p>{issue.description}</p>
                          </div>
                          <div className="ah-recent-footer">
                            <span>{new Date(issue.reportedAt).toLocaleDateString()}</span>
                            <span>Tech: {issue.technician}</span>
                          </div>
                        </div>
                      ))}
                    </div>
                  ) : <p className="ah-no-data">No issues reported yet</p>}
                </div>
              </div>

          {/* Schedule Maintenance Modal */}
          {scheduleAsset && (
            <ScheduleMaintenance
              asset={{ _id: scheduleAsset._id, assetName: scheduleAsset.name, labLocation: '', maintenanceFrequency: scheduleAsset.frequency }}
              onClose={() => setScheduleAsset(null)}
              onScheduled={() => { setScheduleAsset(null); fetchData(); }}
            />
          )}

          {/* Breakdown Requests Modal */}
          {showBreakdownRequests && (
            <div className="ah-modal-overlay" onClick={() => setShowBreakdownRequests(false)}>
              <div className="ah-modal-content" onClick={(e) => e.stopPropagation()}>
                <BreakdownRequests
                  user={user}
                  onClose={() => setShowBreakdownRequests(false)}
                />
              </div>
            </div>
          )}

          {/* Extension & Release Requests Modal */}
          {showExtensionReleaseRequests && (
            <div className="ah-modal-overlay" onClick={() => setShowExtensionReleaseRequests(false)}>
              <div className="ah-modal-content" onClick={(e) => e.stopPropagation()}>
                <ExtensionReleaseRequests
                  user={user}
                  onClose={() => setShowExtensionReleaseRequests(false)}
                />
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default AssistantHomeDashboard;
